import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import _get from 'lodash/get';
import { Site } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useModalManagement from '@lib/hooks/useModalManagement';
import { Modals } from '@lib/enums/modals';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Map from '@lib/components/Map/Map';
import { MapParams } from '@lib/components/Map/types';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import Search from '@lib/components/Search/Search';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { IconName } from '@lib/components/Modal/enums';
import { APP_URLS } from 'constants/urls';
import { SiteListView } from 'routes/Sites/enums';
import Table from '@lib/components/Table/Table';
import getSitesTableActionsMenu from 'routes/Sites/utils/getSitesTableActionsMenu';
import getSitesTableColumns from 'routes/Sites/utils/getSitesTableColumns';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import {
  SITE_LIST_VIEW,
  SITE_MAP_VIEW_PARAMS,
} from '@lib/enums/localStorageKeys';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import Trans from '@lib/components/Trans/Trans';
import { MapSize } from '@lib/components/Map/enums';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';
import { handleCenterMap } from '@lib/utils/mapUtils';
import SiteMapPopupContentAsyncWrap from 'components/SiteMapPopupContent/SiteMapPopupContentAsyncWrap';
import useSiteListActions from 'routes/Sites/utils/useSiteListActions';
import SiteListFilter from 'routes/Sites/SiteListFilter/SiteListFilter';
import SiteListGridView from 'routes/Sites/SiteListGridView/SiteListGridView';
import TableCounter from '@lib/components/Table/components/TableCounter/TableCounter';

const modals = [Modals.SiteListFilter, Modals.SiteDestroyModal];

function SiteListContainer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const mapRef = useRef<L.Map>(null);
  const [mapParams, setMapParams] = useLocalStorage<MapParams>(
    SITE_MAP_VIEW_PARAMS,
    null,
  );
  const [view, setView] = useLocalStorage(SITE_LIST_VIEW, SiteListView.Grid);
  const useBulkSelectResult = useTableItemsBulkSelect<Site>();
  const useModalManagementResult = useModalManagement(modals);
  const { isModalOpened, closeModal } = useModalManagementResult;

  const {
    clearFilter,
    closeFilter,
    destroyLoading,
    filterParams,
    isEmptyState,
    isEmptyStateByFilter,
    isEmptyStateVisible,
    isPaginationVisible,
    isViewOnly,
    onConfirmedDelete,
    onDeleteHandler,
    onEditHandler,
    onFilterSubmit,
    onViewHandler,
    openFilter,
    resetAllFilters,
    sectorsLoadOptions,
    siteLocationsQueryHookResult,
    sitesQueryHookResult,
    siteLocationCenterQueryHookResult,
  } = useSiteListActions({
    view,
    mapParams,
    useModalManagementResult,
    useBulkSelectResult,
  });

  const {
    data: siteLocationCenter,
    firstLoading: siteLocationCenterLoading,
    refetch: siteLocationCenterRefetch,
  } = siteLocationCenterQueryHookResult;

  const onCenterMap = () => {
    siteLocationCenterRefetch().then((res) => {
      handleCenterMap({ res, mapRef });
    });
  };

  const newCenter =
    siteLocationCenter?.lon && siteLocationCenter?.lat
      ? { lng: siteLocationCenter.lon, lat: siteLocationCenter.lat }
      : null;

  const tableColumns = getSitesTableColumns({
    onViewHandler,
    t,
  });
  const tableActionsMenu = getSitesTableActionsMenu({
    onEditHandler,
    onViewHandler,
    onDeleteHandler,
    isViewOnly,
    t,
  });

  if (sitesQueryHookResult.error) {
    return (
      <ErrorPage
        status={getStatusFromError(sitesQueryHookResult.error)}
        description={getDescriptionFromError(sitesQueryHookResult.error)}
      />
    );
  }

  if (siteLocationsQueryHookResult.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteLocationsQueryHookResult.error)}
        description={getDescriptionFromError(
          siteLocationsQueryHookResult.error,
        )}
      />
    );
  }

  const mapData = siteLocationsQueryHookResult.data
    ? removeTypenameDeep(siteLocationsQueryHookResult.data)
    : undefined;

  const emptyState = (
    <div>
      <TableCounter
        totalEntries={0}
        counterText={t('site-plural', {
          count: 0,
        })}
      />
      {isEmptyStateByFilter && (
        <EmptyState
          title={t('no-results-found')}
          text={t('no-results-found-text')}
          icon={IconName.SearchOff}
          actions={[
            <Button
              key="back"
              buttonText={t('go-back')}
              buttonType={ButtonTypes.primaryFilled}
              leftIcon="arrow_back"
              onClick={resetAllFilters}
            />,
          ]}
        />
      )}
      {isEmptyState && (
        <EmptyState
          title={t('no-sites-yet')}
          text={t('no-sites-text')}
          icon={IconName.Cancel}
          actions={
            isViewOnly
              ? []
              : [
                  <Button
                    key="back"
                    buttonText={t('create-site')}
                    buttonType={ButtonTypes.primaryFilled}
                    leftIcon="add"
                    to={APP_URLS.dashboard.sites.add.path}
                  />,
                ]
          }
        />
      )}
    </div>
  );
  const tableEmptyState = (
    <ListEmptyState
      title={t('no-sites-yet')}
      text={t('no-sites-text')}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="add"
                buttonText={t('create-new-site')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                to={APP_URLS.dashboard.sites.add.path}
              />,
            ]
      }
    />
  );

  const popupContentRender = (renderProps) => {
    const siteId = _get(renderProps, ['properties', 'sourceables', 0, 'id']);
    return <SiteMapPopupContentAsyncWrap siteId={siteId} {...renderProps} />;
  };

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageHeader title={t('sites')}>
        {isViewOnly ? (
          <ViewOnlyChip />
        ) : (
          <Button
            buttonText={t('create-new-site')}
            data-test-id="sites__add-button"
            leftIcon="add"
            to={APP_URLS.dashboard.sites.add.path}
          />
        )}
      </PageHeader>
      <SiteListFilter
        clearFilter={clearFilter}
        closeFilter={closeFilter}
        filterParams={filterParams}
        isFilterModalOpen={isModalOpened(Modals.SiteListFilter)}
        onFilterSubmit={onFilterSubmit}
        openFilter={openFilter}
        sectorsLoadOptions={sectorsLoadOptions}
        setView={setView}
        view={view}
      />
      <PageBody>
        <>
          <div>
            {!isEmptyState && (
              <Search
                placeholder={t('sites-search-placeholder')}
                data-test-id="sites__search-input"
              />
            )}
          </div>
          {view === SiteListView.Map && (
            <div>
              {siteLocationCenterLoading ? (
                <CircleLoader />
              ) : (
                <>
                  <TableCounter
                    totalEntries={sitesQueryHookResult.totalCount}
                    counterText={t('site-plural', {
                      count: sitesQueryHookResult.totalCount,
                    })}
                  />
                  <Map
                    defaultCenter={newCenter}
                    disableAutoFit
                    mapParams={mapParams}
                    mapRef={mapRef}
                    onCenterMap={onCenterMap}
                    PopupContent={popupContentRender}
                    setMapParams={setMapParams}
                    size={MapSize.Large}
                    value={mapData}
                  />
                </>
              )}
            </div>
          )}
          {(view === SiteListView.Grid || view === SiteListView.Table) && (
            <div>
              {isEmptyStateVisible ? (
                emptyState
              ) : (
                <div>
                  {sitesQueryHookResult.firstLoading ? (
                    <CircleLoader />
                  ) : (
                    <CircleLoaderWrapper
                      isLoading={sitesQueryHookResult.loading}
                      type={CircleLoaderWrapperType.blur}
                    >
                      {view === SiteListView.Grid && (
                        <>
                          <TableCounter
                            totalEntries={sitesQueryHookResult.totalCount}
                            counterText={t('site-plural', {
                              count: sitesQueryHookResult.totalCount,
                            })}
                          />
                          <SiteListGridView
                            collection={sitesQueryHookResult.collection}
                            isPaginationVisible={isPaginationVisible}
                            totalCount={sitesQueryHookResult.totalCount}
                          />
                        </>
                      )}
                      {view === SiteListView.Table && (
                        <Table
                          actionsMenu={tableActionsMenu}
                          columns={tableColumns}
                          counterText={t('site-plural', {
                            count: sitesQueryHookResult.totalCount,
                          })}
                          data={sitesQueryHookResult.collection}
                          emptyState={tableEmptyState}
                          id="sites"
                          loading={sitesQueryHookResult.loading}
                          totalEntries={sitesQueryHookResult.totalCount}
                        />
                      )}
                    </CircleLoaderWrapper>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      </PageBody>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-site-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.SiteDestroyModal)}
        loading={destroyLoading}
        onClose={() => closeModal(Modals.SiteDestroyModal)}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
    </>
  );
}

export default SiteListContainer;
